import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  ChevronLeft,
  ChevronRight,
  Send,
  X,
  XCircle,
  Award,
  TrendingUp,
  FileText,
  ZoomIn
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import useAuthStore from '../../store/authStore';
import respuestaService from '../../services/respuestaService';
import usuarioExamenService from '../../services/usuarioExamenService';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Modal from '../../components/ui/Modal';
import AuthImage from '../../components/common/AuthImage';
import ImageViewer from '../../components/common/ImageViewer';
import PantallasBienvenida from '../../components/exam/PantallasBienvenida';

const RealizarExamen = ({ examen, perfil, proceso, onFinalizar }) => {
  const { user } = useAuthStore();
  // Si tiempoMinutos es 0, significa sin límite de tiempo (usar null para indicarlo)
  const tiempoInicial = examen.tiempoMinutos > 0 ? examen.tiempoMinutos * 60 : null;
  const [tiempoRestante, setTiempoRestante] = useState(tiempoInicial); // en segundos o null
  const [preguntaActual, setPreguntaActual] = useState(0);
  const [respuestas, setRespuestas] = useState({});
  const [showPrevisualizacion, setShowPrevisualizacion] = useState(false);
  const [examenIniciado, setExamenIniciado] = useState(false);
  const [mostrarPantallasInicio, setMostrarPantallasInicio] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [guardandoProgreso, setGuardandoProgreso] = useState(false);
  const [estadoUsuarioExamen, setEstadoUsuarioExamen] = useState(null);
  const [cargandoEstado, setCargandoEstado] = useState(true);
  const [imagenViewer, setImagenViewer] = useState(null);
  
  // Base URL para las imágenes
  const BASE_IMAGE_URL = 'https://skills.doms.pe/';

  // Temporizador (solo si hay límite de tiempo)
  useEffect(() => {
    if (!examenIniciado || tiempoRestante === null) return;

    if (tiempoRestante <= 0) {
      handleTiempoAgotado();
      return;
    }

    const timer = setInterval(() => {
      setTiempoRestante(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [tiempoRestante, examenIniciado]);

  // Verificar estado del usuario-examen al cargar el componente
  useEffect(() => {
    verificarEstadoExamen();
  }, []);

  // Cargar progreso existente cuando se inicie el examen
  useEffect(() => {
    if (examenIniciado && examen.preguntas.length > 0 && examen) {
      cargarProgresoExistente();
    }
  }, [examenIniciado, examen]);

  const verificarEstadoExamen = async () => {
    try {
      setCargandoEstado(true);
      const progreso = await usuarioExamenService.obtenerProgreso(user.id, examen.id);
      setEstadoUsuarioExamen(progreso);
      
      // Si el examen ya está finalizado, mostrar mensaje y no permitir acceso
      if (progreso && (progreso.estado === 'FINALIZADO' || progreso.estado === 'COMPLETADO')) {
        toast.info('Este examen ya fue completado anteriormente');
        setTimeout(() => {
          onFinalizar();
        }, 2000);
      }
    } catch (error) {
      console.error('Error al verificar estado del examen:', error);
      // Si no existe el registro, continuar normalmente (examen no iniciado)
      setEstadoUsuarioExamen(null);
    } finally {
      setCargandoEstado(false);
    }
  };

  const formatearTiempo = (segundos) => {
    const minutos = Math.floor(segundos / 60);
    const segs = segundos % 60;
    return `${minutos.toString().padStart(2, '0')}:${segs.toString().padStart(2, '0')}`;
  };

  const handleSeleccionarRespuesta = async (preguntaId, opcionId) => {
    setRespuestas(prev => ({
      ...prev,
      [preguntaId]: opcionId
    }));

    // Guardar progreso automáticamente
    setGuardandoProgreso(true);
    try {
      await respuestaService.guardarProgresoExamen({
        usuarioId: user.id,
        examenId: examen.id,
        preguntaId: preguntaId,
        opcionId: opcionId
      });
    } catch (error) {
      console.error('Error al guardar progreso:', error);
      // No mostrar toast para no interrumpir el flujo del examen
    } finally {
      setGuardandoProgreso(false);
    }
  };

  // Cargar progreso existente al iniciar el examen
  const cargarProgresoExistente = async () => {
    try {
      const progreso = await respuestaService.obtenerProgresoExamen(user.id, examen.id);
      if (progreso && progreso.length > 0) {
        const respuestasGuardadas = {};
        progreso.forEach(item => {
          // Solo cargar respuestas que tengan opcionId (no null)
          if (item.opcionId !== null) {
            respuestasGuardadas[item.preguntaId] = item.opcionId;
          }
        });
        setRespuestas(respuestasGuardadas);
      }
    } catch (error) {
      console.error('Error al cargar progreso existente:', error);
      // No mostrar toast para no interrumpir el flujo del examen
    }
  };

  const handleTiempoAgotado = async () => {
    try {
      toast.error('⏰ Se ha agotado el tiempo del examen');
      // Marcar como tiempo agotado en la tabla intermedia
      await usuarioExamenService.tiempoAgotado(user.id, examen.id);
      console.log('⏰ Estado del usuario-examen cambiado a TIEMPO_AGOTADO');
    } catch (error) {
      console.error('⚠️ Error al marcar tiempo agotado:', error);
    }
    // Enviar el examen de todas formas
    await handleEnviarExamen();
  };

  const handleEnviarExamen = async () => {
    try {
      setEnviando(true);
      
      // Preparar las respuestas finales para enviar (solo las que están en el estado local)
      const respuestasArray = Object.entries(respuestas).map(([preguntaId, opcionId]) => {
        // Encontrar la pregunta y la opción seleccionada
        const pregunta = examen.preguntas.find(p => p.id === parseInt(preguntaId));
        const opcion = pregunta?.opciones?.find(o => o.id === opcionId);
        
        return {
          usuarioId: user.id,
          examenId: examen.id,
          preguntaId: parseInt(preguntaId),
          opcionId: opcionId,
          correcta: opcion?.esCorrecta || false
        };
      });

      // Enviar todas las respuestas finales
      await respuestaService.enviarRespuestasExamen(respuestasArray);

      // Calcular puntaje obtenido y máximo
      const respuestasCorrectas = respuestasArray.filter(r => r.correcta).length;
      const totalPreguntas = examen.preguntas.length;
      const puntajeMaximo = examen.preguntas.reduce((sum, p) => sum + (p.peso || 1), 0);
      const puntajeObtenido = examen.preguntas
        .filter(p => respuestasArray.find(r => r.preguntaId === p.id && r.correcta))
        .reduce((sum, p) => sum + (p.peso || 1), 0);

      // Cambiar estado del usuario-examen a FINALIZADO
      try {
        await usuarioExamenService.finalizarExamen(
          user.id, 
          examen.id, 
          puntajeObtenido, 
          puntajeMaximo,
          `${respuestasCorrectas}/${totalPreguntas} respuestas correctas`
        );
        console.log('✅ Estado del usuario-examen cambiado a FINALIZADO');
      } catch (errorEstado) {
        console.error('⚠️ Error al cambiar estado del usuario-examen:', errorEstado);
        // Continuar de todas formas con los resultados
      }

      // Mostrar mensaje de éxito y finalizar
      toast.success('✅ Examen enviado correctamente');
      setTimeout(() => {
        onFinalizar();
      }, 1500);
    } catch (error) {
      console.error('Error al enviar examen:', error);
      toast.error('Error al enviar el examen. Por favor intenta de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  const getPreguntasRespondidas = () => {
    return Object.keys(respuestas).length;
  };

  const pregunta = examen.preguntas[preguntaActual];
  const isUltimaPregunta = preguntaActual === examen.preguntas.length - 1;
  const isPrimeraPregunta = preguntaActual === 0;

  // Mostrar loader mientras se verifica el estado
  if (cargandoEstado) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600">Verificando estado del examen...</p>
        </div>
      </div>
    );
  }

  // Si el examen ya está finalizado, mostrar mensaje
  if (estadoUsuarioExamen && (estadoUsuarioExamen.estado === 'FINALIZADO' || estadoUsuarioExamen.estado === 'COMPLETADO')) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <Card>
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-success" />
            </div>
            <h1 className="text-2xl font-bold text-neutral-900 mb-2">
              Examen Finalizado
            </h1>
            <p className="text-neutral-600 mb-4">
              Ya has completado este examen anteriormente
            </p>
            
            {estadoUsuarioExamen.puntajeObtenido !== null && (
              <div className="bg-neutral-50 rounded-lg p-6 max-w-md mx-auto mb-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-neutral-600 mb-1">Puntaje Obtenido</p>
                    <p className="text-2xl font-bold text-primary">
                      {estadoUsuarioExamen.puntajeObtenido}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-neutral-600 mb-1">Puntaje Máximo</p>
                    <p className="text-2xl font-bold text-neutral-900">
                      {estadoUsuarioExamen.puntajeMaximo}
                    </p>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-neutral-200">
                  <p className="text-sm text-neutral-600 mb-1">Porcentaje</p>
                  <p className="text-3xl font-bold text-success">
                    {((estadoUsuarioExamen.puntajeObtenido / estadoUsuarioExamen.puntajeMaximo) * 100).toFixed(1)}%
                  </p>
                </div>
              </div>
            )}

            <Button variant="primary" onClick={onFinalizar}>
              Volver a Mis Exámenes
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Pantallas de bienvenida e instrucciones
  if (mostrarPantallasInicio) {
    return (
      <PantallasBienvenida
        nombreProceso={proceso.nombre}
        nombreExamen={examen.nombre}
        descripcionExamen={examen.descripcion}
        tiempoMinutos={examen.tiempoMinutos}
        cantidadPreguntas={examen.preguntas.length}
        ponderacion={examen.porcentajePonderacion}
        tipoExamen={examen.tipoExamen || 'EXAMEN'}
        onIniciar={async () => {
          try {
            await usuarioExamenService.iniciarExamen(user.id, examen.id);
            console.log('✅ Estado del usuario-examen cambiado a EN_PROGRESO');
          } catch (error) {
            console.error('⚠️ Error al cambiar estado del usuario-examen:', error);
          }
          setMostrarPantallasInicio(false);
          setExamenIniciado(true);
        }}
        onCancelar={onFinalizar}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header con información del examen */}
      <Card>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-neutral-900">{examen.nombre}</h1>
            <p className="text-sm text-neutral-600">
              {proceso.nombre} - {perfil.nombre}
            </p>
          </div>
          <div className="flex items-center gap-4">
            {tiempoRestante !== null ? (
              <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
                tiempoRestante < 300 ? 'bg-danger/10 text-danger' : 'bg-primary/10 text-primary'
              }`}>
                <Clock className="w-5 h-5" />
                <span className="text-lg font-mono font-bold">
                  {formatearTiempo(tiempoRestante)}
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-success/10 text-success">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-bold">Sin límite de tiempo</span>
              </div>
            )}
            {guardandoProgreso && (
              <div className="flex items-center gap-2 px-3 py-2 bg-info/10 text-info rounded-lg">
                <div className="w-4 h-4 border-2 border-info border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm font-medium">Guardando...</span>
              </div>
            )}
            <Badge variant="secondary">
              {getPreguntasRespondidas()} / {examen.preguntas.length} respondidas
            </Badge>
          </div>
        </div>
      </Card>

      {/* Pregunta actual */}
      <Card>
        <AnimatePresence mode="wait">
          <motion.div
            key={preguntaActual}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {/* Número de pregunta */}
            <div className="flex items-center justify-between mb-4">
              <Badge variant="primary">
                Pregunta {preguntaActual + 1} de {examen.preguntas.length}
              </Badge>
              <Badge variant={pregunta.tipo === 'verdadero_falso' ? 'secondary' : 'info'}>
                {pregunta.tipo === 'verdadero_falso' ? 'Verdadero/Falso' : 
                 pregunta.tipo === 'multiple_choice' ? 'Opción Múltiple' : pregunta.tipo}
              </Badge>
            </div>

            {/* Texto de la pregunta */}
            <div className="mb-6">
              <h2 className="text-xl font-bold text-neutral-900 mb-3 leading-relaxed">
                {pregunta.texto}
              </h2>
              {pregunta.peso && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-sm font-medium">
                  <Award className="w-4 h-4" />
                  {pregunta.peso} puntos
                </div>
              )}
              {pregunta.imagen && (
                <div className="mt-5 flex justify-center bg-gradient-to-br from-neutral-50 to-neutral-100 rounded-2xl p-6 border border-neutral-200">
                  <ImageViewer 
                    src={pregunta.imagen} 
                    alt="Imagen de la pregunta" 
                    className="w-full h-80 object-contain rounded-xl shadow-lg bg-white"
                    containerClassName="w-full max-w-3xl"
                  />
                </div>
              )}
            </div>

            {/* Opciones */}
            <div className="space-y-3">
              {pregunta.opciones && pregunta.opciones.map((opcion) => {
                const isSeleccionada = respuestas[pregunta.id] === opcion.id;
                
                return (
                  <motion.div
                    key={opcion.id}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    <button
                      onClick={() => handleSeleccionarRespuesta(pregunta.id, opcion.id)}
                      className={`w-full text-left p-5 rounded-xl border-2 transition-all duration-200 group ${
                        isSeleccionada
                          ? 'border-primary bg-gradient-to-br from-primary/10 to-primary/5 shadow-md'
                          : 'border-neutral-200 hover:border-primary/50 hover:shadow-sm bg-white'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                          isSeleccionada
                            ? 'border-primary bg-primary scale-110'
                            : 'border-neutral-300 group-hover:border-primary/50'
                        }`}>
                          {isSeleccionada && (
                            <div className="w-3 h-3 bg-white rounded-full"></div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <span className={`block text-base leading-relaxed ${
                            isSeleccionada ? 'text-neutral-900 font-semibold' : 'text-neutral-700 font-medium group-hover:text-neutral-900'
                          }`}>
                            {opcion.texto}
                          </span>
                          {opcion.imagen && (
                            <div className="mt-4 flex justify-center bg-neutral-50 rounded-xl p-3 border border-neutral-200">
                              <ImageViewer 
                                src={opcion.imagen} 
                                alt="Imagen de la opción" 
                                className="w-full h-48 object-contain rounded-lg shadow-md bg-white"
                                containerClassName="w-full max-w-lg"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  </motion.div>
                );
              })}
            </div>

            {/* Navegación */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-neutral-200">
              <Button
                variant="outline"
                onClick={async () => {
                  // Guardar progreso con opcionId null si no hay respuesta para marcar que fue visitada
                  if (!respuestas[pregunta.id]) {
                    setGuardandoProgreso(true);
                    try {
                      await respuestaService.guardarProgresoExamen({
                        usuarioId: user.id,
                        examenId: examen.id,
                        preguntaId: pregunta.id,
                        opcionId: null // Marcar como visitada pero sin respuesta
                      });
                    } catch (error) {
                      console.error('Error al guardar progreso vacío:', error);
                    } finally {
                      setGuardandoProgreso(false);
                    }
                  }
                  setPreguntaActual(prev => prev - 1);
                }}
                disabled={isPrimeraPregunta}
              >
                <ChevronLeft className="w-4 h-4" />
                Anterior
              </Button>

              <div className="flex gap-2">
                {examen.preguntas.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setPreguntaActual(index)}
                    className={`w-8 h-8 rounded-full text-sm font-medium transition-all ${
                      index === preguntaActual
                        ? 'bg-primary text-white'
                        : respuestas[examen.preguntas[index].id]
                        ? 'bg-success text-white'
                        : 'bg-neutral-200 text-neutral-600 hover:bg-neutral-300'
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
              </div>

              {isUltimaPregunta ? (
                <Button
                  variant="success"
                  onClick={() => setShowPrevisualizacion(true)}
                >
                  <Send className="w-4 h-4" />
                  Enviar Examen
                </Button>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setPreguntaActual(prev => prev + 1)}
                >
                  Siguiente
                  <ChevronRight className="w-4 h-4" />
                </Button>
              )}
            </div>
          </motion.div>
        </AnimatePresence>
      </Card>

      {/* Modal de Previsualización */}
      <Modal
        isOpen={showPrevisualizacion}
        onClose={() => setShowPrevisualizacion(false)}
        title="Revisión Final - Previsualiza tus respuestas"
        size="xl"
      >
        <div className="space-y-4">
          <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-warning flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-warning mb-1">Revisa tus respuestas</p>
                <p className="text-sm text-neutral-700">
                  Una vez enviado el examen, no podrás modificar tus respuestas. Revisa cuidadosamente antes de confirmar.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-neutral-50 rounded-lg p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">Preguntas respondidas:</span>
              <span className="font-medium text-neutral-900">
                {getPreguntasRespondidas()} / {examen.preguntas.length}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-neutral-600">Tiempo restante:</span>
              <span className="font-medium text-neutral-900">
                {formatearTiempo(tiempoRestante)}
              </span>
            </div>
          </div>

          {getPreguntasRespondidas() < examen.preguntas.length ? (
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-red-800 mb-1">
                    ⚠️ Atención: Tienes preguntas sin responder
                  </p>
                  <p className="text-sm text-red-700">
                    Faltan <strong>{examen.preguntas.length - getPreguntasRespondidas()} pregunta(s)</strong> por responder.
                    Puedes volver a revisar o enviar el examen con las respuestas actuales.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-green-800 mb-1">
                    ✓ Excelente: Todas las preguntas respondidas
                  </p>
                  <p className="text-sm text-green-700">
                    Has respondido todas las preguntas. Puedes revisar tus respuestas o confirmar el envío.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Lista de respuestas - Vista Simplificada */}
          <div className="space-y-2">
            <h3 className="text-lg font-semibold text-neutral-800 flex items-center gap-2 mb-3">
              <FileText className="w-5 h-5" />
              Tus respuestas seleccionadas
            </h3>
            
            <div className="bg-neutral-50 rounded-lg p-4 max-h-96 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {examen.preguntas.map((pregunta, index) => {
                  const respuesta = respuestas[pregunta.id];
                  
                  return (
                    <div 
                      key={pregunta.id}
                      className={`flex items-center justify-between p-3 rounded-lg border-l-4 ${
                        respuesta 
                          ? 'bg-white border-l-green-500' 
                          : 'bg-yellow-50 border-l-yellow-500'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-bold text-neutral-700">
                          PREGUNTA {index + 1}:
                        </span>
                        {respuesta ? (
                          <div className="flex items-center gap-2 text-green-700">
                            <CheckCircle className="w-4 h-4" />
                            <span className="font-medium text-sm">Con respuesta</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 text-yellow-700">
                            <XCircle className="w-4 h-4" />
                            <span className="font-medium text-sm">En blanco</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-neutral-200">
            <Button
              variant="outline"
              onClick={() => setShowPrevisualizacion(false)}
              disabled={enviando}
            >
              Volver a Revisar
            </Button>
            <Button
              variant="primary"
              onClick={handleEnviarExamen}
              disabled={enviando}
            >
              {enviando ? 'Enviando...' : 'Confirmar Envío'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Visor de Imágenes */}
      {imagenViewer && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setImagenViewer(null)}
        >
          <div className="relative max-w-5xl max-h-screen">
            <button
              onClick={() => setImagenViewer(null)}
              className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={imagenViewer.url}
              alt={imagenViewer.alt}
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default RealizarExamen;
